import { useMemo, useRef, useState, type ReactNode } from 'react';
import { addDays, differenceInCalendarDays, eachDayOfInterval, endOfMonth, endOfWeek, format, isWeekend, max as maxDate, min as minDate, parseISO, startOfMonth, startOfWeek } from 'date-fns';
import { Button, IconButton } from '../design/primitives/Button';
import { Tabs } from '../design/primitives/Tabs';
import { EmptyState } from '../design/primitives/Misc';

// Timeline (gantt): one row per item, bars from start→end, dependency arrows (SVG overlay),
// zoom day/week/month, drag to move, drag the right edge to resize. Dates are ISO
// `YYYY-MM-DD`; onChange emits the new start/end pair after a drag completes.

export type Zoom = 'day' | 'week' | 'month';

export interface TimelineItem {
  id: string;
  label: ReactNode;
  start: string | null;
  end: string | null;
  /** 0..1 */
  progress?: number;
  /** ids this item depends on (arrow from dependency → this). */
  dependsOn?: string[];
  group?: string;
  tone?: 'accent' | 'pos' | 'warn' | 'neg' | 'muted';
}

export interface TimelineProps {
  items: TimelineItem[];
  onChange?: (id: string, start: string, end: string) => void | Promise<void>;
  onOpen?: (id: string) => void;
  zoom?: Zoom;
  onZoomChange?: (z: Zoom) => void;
  /** px per day at each zoom. */
  scale?: Record<Zoom, number>;
  empty?: ReactNode;
  today?: Date;
}

const DEFAULT_SCALE: Record<Zoom, number> = { day: 44, week: 18, month: 6 };
const ROW = 34;
const LABEL_W = 220;
const HEAD_H = 44;

export function Timeline({ items, onChange, onOpen, zoom: zoomProp, onZoomChange, scale = DEFAULT_SCALE, empty, today = new Date() }: TimelineProps) {
  const [zoomLocal, setZoomLocal] = useState<Zoom>('week');
  const zoom = zoomProp ?? zoomLocal;
  const setZoom = (z: Zoom) => (onZoomChange ? onZoomChange(z) : setZoomLocal(z));
  const px = scale[zoom];
  const scrollRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ id: string; mode: 'move' | 'resize'; x0: number; start: Date; end: Date; dStart: Date; dEnd: Date } | null>(null);

  const dated = items.filter((i) => i.start && i.end);
  const range = useMemo(() => {
    if (dated.length === 0) return { from: startOfWeek(addDays(today, -7), { weekStartsOn: 1 }), to: endOfWeek(addDays(today, 28), { weekStartsOn: 1 }) };
    const starts = dated.map((i) => parseISO(i.start!));
    const ends = dated.map((i) => parseISO(i.end!));
    const from = addDays(minDate([...starts, today]), -7);
    const to = addDays(maxDate([...ends, today]), 14);
    return zoom === 'month' ? { from: startOfMonth(from), to: endOfMonth(to) } : { from: startOfWeek(from, { weekStartsOn: 1 }), to: endOfWeek(to, { weekStartsOn: 1 }) };
  }, [dated, today, zoom]);

  const days = useMemo(() => eachDayOfInterval({ start: range.from, end: range.to }), [range]);
  const width = days.length * px;
  const x = (d: Date) => differenceInCalendarDays(d, range.from) * px;

  // Header ticks: days (zoom day), weeks (zoom week), months (zoom month).
  const ticks = useMemo(() => {
    const out: { x: number; w: number; label: string; major?: boolean }[] = [];
    if (zoom === 'day') for (const d of days) out.push({ x: x(d), w: px, label: format(d, 'd'), major: d.getDate() === 1 });
    else if (zoom === 'week') {
      let cur = startOfWeek(range.from, { weekStartsOn: 1 });
      while (cur <= range.to) {
        out.push({ x: x(cur), w: 7 * px, label: format(cur, 'd MMM'), major: cur.getDate() <= 7 });
        cur = addDays(cur, 7);
      }
    } else {
      let cur = startOfMonth(range.from);
      while (cur <= range.to) {
        const end = endOfMonth(cur);
        out.push({ x: x(cur), w: (differenceInCalendarDays(end, cur) + 1) * px, label: format(cur, 'MMM yyyy'), major: true });
        cur = addDays(end, 1);
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, zoom, px, range]);
  const months = useMemo(() => {
    const out: { x: number; label: string }[] = [];
    let cur = startOfMonth(range.from);
    while (cur <= range.to) {
      out.push({ x: Math.max(0, x(cur)), label: format(cur, 'MMMM yyyy') });
      cur = addDays(endOfMonth(cur), 1);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, px]);

  const index = new Map(items.map((it, i) => [it.id, i]));
  const geometry = (it: TimelineItem) => {
    const d = drag?.id === it.id ? drag : null;
    const s = d ? d.dStart : parseISO(it.start!);
    const e = d ? d.dEnd : parseISO(it.end!);
    return { left: x(s), width: Math.max(px, (differenceInCalendarDays(e, s) + 1) * px), s, e };
  };

  const startDrag = (it: TimelineItem, mode: 'move' | 'resize', ev: React.PointerEvent) => {
    if (!onChange || !it.start || !it.end) return;
    ev.preventDefault();
    (ev.target as HTMLElement).setPointerCapture(ev.pointerId);
    const start = parseISO(it.start);
    const end = parseISO(it.end);
    setDrag({ id: it.id, mode, x0: ev.clientX, start, end, dStart: start, dEnd: end });
  };
  const onMove = (ev: React.PointerEvent) => {
    if (!drag) return;
    const dd = Math.round((ev.clientX - drag.x0) / px);
    if (drag.mode === 'move') setDrag({ ...drag, dStart: addDays(drag.start, dd), dEnd: addDays(drag.end, dd) });
    else setDrag({ ...drag, dEnd: maxDate([drag.start, addDays(drag.end, dd)]) });
  };
  const endDrag = () => {
    if (!drag) return;
    const changed = drag.dStart.getTime() !== drag.start.getTime() || drag.dEnd.getTime() !== drag.end.getTime();
    if (changed) void onChange?.(drag.id, format(drag.dStart, 'yyyy-MM-dd'), format(drag.dEnd, 'yyyy-MM-dd'));
    setDrag(null);
  };

  if (items.length === 0) return <>{empty ?? <EmptyState title="Nothing scheduled" icon="gantt-chart" />}</>;

  const height = items.length * ROW;
  const todayX = x(today);

  return (
    <div className="hf-timeline">
      <div className="hf-timeline-tools">
        <Tabs variant="pills" value={zoom} onChange={(k) => setZoom(k as Zoom)} tabs={[{ key: 'day', label: 'Day' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }]} aria-label="Zoom" />
        <Button size="sm" variant="ghost" onClick={() => scrollRef.current?.scrollTo({ left: Math.max(0, todayX - 300), behavior: 'smooth' })}>
          Today
        </Button>
        <IconButton icon="zoom-in" label="Zoom in" size="sm" onClick={() => setZoom(zoom === 'month' ? 'week' : 'day')} disabled={zoom === 'day'} />
        <IconButton icon="zoom-out" label="Zoom out" size="sm" onClick={() => setZoom(zoom === 'day' ? 'week' : 'month')} disabled={zoom === 'month'} />
      </div>
      <div className="hf-timeline-body">
        <div className="hf-timeline-labels" style={{ width: LABEL_W }}>
          <div className="head" style={{ height: HEAD_H }} />
          {items.map((it) => (
            <div key={it.id} className="row" style={{ height: ROW }}>
              <button type="button" className="lbl" onClick={() => onOpen?.(it.id)} title={typeof it.label === 'string' ? it.label : undefined}>
                {it.label}
              </button>
              {it.group && <span className="hf-muted grp">{it.group}</span>}
            </div>
          ))}
        </div>
        <div className="hf-timeline-scroll hf-scroll" ref={scrollRef} onPointerMove={onMove} onPointerUp={endDrag} onPointerCancel={endDrag}>
          <div className="hf-timeline-canvas" style={{ width, height: height + HEAD_H }}>
            <div className="hf-timeline-head" style={{ height: HEAD_H }}>
              <div className="months">
                {months.map((m) => (
                  <span key={m.label} style={{ left: m.x }}>
                    {m.label}
                  </span>
                ))}
              </div>
              <div className="ticks">
                {ticks.map((t, i) => (
                  <span key={i} className={t.major ? 'major' : undefined} style={{ left: t.x, width: t.w }}>
                    {t.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="hf-timeline-grid" style={{ top: HEAD_H, height }}>
              {zoom !== 'month' &&
                days.map((d, i) => isWeekend(d) && <i key={i} className="weekend" style={{ left: i * px, width: px }} />)}
              {ticks.map((t, i) => (
                <i key={`t${i}`} className={['tick', t.major && 'major'].filter(Boolean).join(' ')} style={{ left: t.x }} />
              ))}
              {items.map((_, i) => (
                <i key={`r${i}`} className="rowline" style={{ top: (i + 1) * ROW }} />
              ))}
              <i className="today" style={{ left: todayX + px / 2 }} />
              <svg className="deps" width={width} height={height} aria-hidden>
                <defs>
                  <marker id="hf-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0 0 8 4 0 8z" fill="currentColor" />
                  </marker>
                </defs>
                {items.map((it) =>
                  (it.dependsOn ?? []).map((dep) => {
                    const from = items[index.get(dep) ?? -1];
                    if (!from?.start || !from.end || !it.start || !it.end) return null;
                    const a = geometry(from);
                    const b = geometry(it);
                    const y1 = (index.get(from.id)! + 0.5) * ROW;
                    const y2 = (index.get(it.id)! + 0.5) * ROW;
                    const x1 = a.left + a.width;
                    const x2 = b.left;
                    const mid = x1 + 12;
                    const d = x2 > x1 + 20 ? `M${x1} ${y1} H${mid} V${y2} H${x2}` : `M${x1} ${y1} H${x1 + 10} V${y2 - ROW / 2} H${x2 - 10} V${y2} H${x2}`;
                    return <path key={`${dep}->${it.id}`} d={d} fill="none" stroke="currentColor" strokeWidth={1.25} markerEnd="url(#hf-arrow)" />;
                  }),
                )}
              </svg>
              {items.map((it, i) => {
                if (!it.start || !it.end) return null;
                const g = geometry(it);
                const dragging = drag?.id === it.id;
                return (
                  <div
                    key={it.id}
                    className={['hf-bar', it.tone ?? 'accent', dragging && 'dragging', onChange && 'draggable'].filter(Boolean).join(' ')}
                    style={{ left: g.left, width: g.width, top: i * ROW + 6, height: ROW - 12 }}
                    role="button"
                    tabIndex={0}
                    aria-label={`${typeof it.label === 'string' ? it.label : it.id}: ${format(g.s, 'MMM d')} – ${format(g.e, 'MMM d')}`}
                    onPointerDown={(e) => startDrag(it, 'move', e)}
                    onDoubleClick={() => onOpen?.(it.id)}
                    onKeyDown={(e) => {
                      if (!onChange || !it.start || !it.end) return;
                      const s = parseISO(it.start);
                      const en = parseISO(it.end);
                      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                        e.preventDefault();
                        const d = e.key === 'ArrowRight' ? 1 : -1;
                        if (e.shiftKey) void onChange(it.id, it.start, format(maxDate([s, addDays(en, d)]), 'yyyy-MM-dd'));
                        else void onChange(it.id, format(addDays(s, d), 'yyyy-MM-dd'), format(addDays(en, d), 'yyyy-MM-dd'));
                      } else if (e.key === 'Enter') onOpen?.(it.id);
                    }}
                    title={`${format(g.s, 'MMM d')} – ${format(g.e, 'MMM d')}`}
                  >
                    {it.progress != null && <i className="progress" style={{ width: `${Math.max(0, Math.min(1, it.progress)) * 100}%` }} />}
                    <span className="txt">{it.label}</span>
                    {onChange && <span className="handle" onPointerDown={(e) => { e.stopPropagation(); startDrag(it, 'resize', e); }} aria-hidden />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
